import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Session } from '../types/firestoreTypes';
import { EventInput } from '@fullcalendar/core';
import { SESSION_STATUS } from '../types/constants';

// Interface for the hook's return value
interface UseCalendarSessionsReturn {
  events: EventInput[];
  isLoading: boolean;
  error: string | null; // Will hold the translated error string
  refresh: () => Promise<void>;
}

export const useCalendarSessions = (
  start?: Date, // Optional start date for filtering
  end?: Date // Optional end date for filtering
): UseCalendarSessionsReturn => {
  const { t } = useTranslation(); // Get the t function
  const { user } = useAuth();
  const [events, setEvents] = useState<EventInput[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // State holds translated error

  // Helper function to convert Session to FullCalendar EventInput (no translation needed here)
  const sessionToEvent = (session: Session): EventInput => {
    const startDate = session.sessionDateTime.toDate();
    const endDate = new Date(
      startDate.getTime() + session.durationMinutes * 60000
    );

    // Define status-based colors (these are UI styles, not translatable text)
    const statusColors = {
      [SESSION_STATUS.SCHEDULED]: {
        backgroundColor: '#F0F9FF',
        borderColor: '#026AA2',
        textColor: '#026AA2',
      },
      [SESSION_STATUS.COMPLETED]: {
        backgroundColor: '#ECFDF3',
        borderColor: '#027A48',
        textColor: '#027A48',
      },
      [SESSION_STATUS.CANCELLED]: {
        backgroundColor: '#FEF3F2',
        borderColor: '#B42318',
        textColor: '#B42318',
      },
      [SESSION_STATUS.NO_SHOW]: {
        backgroundColor: '#FFF6ED',
        borderColor: '#B93815',
        textColor: '#B93815',
      },
      [SESSION_STATUS.INVOICED]: {
        backgroundColor: '#F9F5FF',
        borderColor: '#6941C6',
        textColor: '#6941C6',
      },
    };

    return {
      id: session.id,
      title: `${session.patientName} - ${t(
        `sessionType.${session.sessionType}`,
        session.sessionType
      )}`, // Translate session type in title
      start: startDate,
      end: endDate,
      allDay: false,
      extendedProps: {
        // Pass original data for potential use in event handlers
        patientId: session.patientId,
        status: session.status,
        type: session.sessionType,
        price: session.price,
        notes: session.notes,
      },
      ...statusColors[session.status], // Apply status-specific colors
    };
  };

  // Fetch sessions function
  const fetchSessions = useCallback(async () => {
    if (!user) {
      console.log('useCalendarSessions: No user, clearing events.');
      setEvents([]);
      setIsLoading(false);
      return;
    }

    console.log('useCalendarSessions: Fetching sessions...');
    setIsLoading(true);
    setError(null); // Clear previous errors

    try {
      // Base query for therapist's sessions
      let baseQuery = query(
        collection(db, 'sessions'),
        where('therapistId', '==', user.uid)
      );

      // Add date range filter if start and end dates are provided
      if (start && end) {
        console.log(
          `Applying date range: ${start.toISOString()} - ${end.toISOString()}`
        );
        baseQuery = query(
          baseQuery,
          where('sessionDateTime', '>=', Timestamp.fromDate(start)),
          where('sessionDateTime', '<=', Timestamp.fromDate(end))
          // Note: Depending on Firestore indexing, adding orderBy might be needed here
          // orderBy('sessionDateTime')
        );
      } else {
        console.log('No date range provided, fetching all sessions for user.');
        // Consider adding a default limit or date range if fetching all is too much
      }

      const snapshot = await getDocs(baseQuery);
      const sessions = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Session)
      );
      console.log(`Fetched ${sessions.length} sessions.`);

      // Convert sessions to FullCalendar events using the helper
      const calendarEvents = sessions.map(sessionToEvent);
      setEvents(calendarEvents);
    } catch (err) {
      console.error('Error fetching sessions for calendar:', err);
      setError(t('hooks.useCalendarSessions.errors.loadFailed')); // Set translated error
    } finally {
      setIsLoading(false);
    }
    // Add t to dependency array
  }, [user, start, end, t]); // Dependencies for fetching

  // Initial fetch only - no auto-refresh
  useEffect(() => {
    fetchSessions(); // Fetch on mount or when dependencies change
  }, [fetchSessions]); // Rerun effect if fetchSessions function instance changes

  // Expose refresh function for manual updates
  const refresh = useCallback(async () => {
    console.log('useCalendarSessions: Manual refresh triggered.');
    await fetchSessions();
  }, [fetchSessions]);

  // Return state and refresh function
  return {
    events,
    isLoading,
    error, // Translated error string
    refresh,
  };
};
